import * as mixpanel from 'mixpanel-browser';

import { getUserProfile } from './AuthUtil';
import { getFormattedDeviceDetails } from './DeviceDetailsUtil';
import { getLabels } from './MixPanelLabels';
import { ISubmissionListingDownloadAnalytics } from './submissionListingDownloadAnalytics';

mixpanel.init(process.env.MIXPANEL_TOKEN);

mixpanel.init(process.env.ELEVIO_MIXPANEL_TOKEN, {}, 'elevioMixpanel');

/** Mixpanel property key for agency segmentation. Do not rename — only the value source changes. */
export const MIXPANEL_AGENCY_NAME_PROP = 'Agency Name';

export const MIXPANEL_AGENCY_ID_PROP = 'Agency ID';
export const MIXPANEL_USER_ID_PROP = 'User ID';
export const MIXPANEL_USER_ROLE_PROP = 'User Role';
export const MIXPANEL_SIGNED_UP_STATE_PROP = 'Signed-up State';

export type StaffTextMixpanelRole = 'super_admin' | 'comcate_admin' | 'site_admin' | 'crm_product_admin' | 'crm_user';

/** Mixpanel property key for CRM submission id on staff events. */
export const MIXPANEL_SUBMISSION_ID_PROP = 'submission_id';

/** Mixpanel property key for whether the citizen was notified on close). */
export const MIXPANEL_NOTIFIED_PROP = 'notified';

/** Mixpanel property key for the canonical closure reason code. */
export const MIXPANEL_CLOSURE_REASON_PROP = 'closure_reason';

/** Mixpanel property key for which surface closed the submission. */
export const MIXPANEL_CLOSE_METHOD_PROP = 'close_method';

/** Mixpanel property key for the Action Center phone match outcome. */
export const MIXPANEL_MATCH_OUTCOME_PROP = 'Match Outcome';

/** Mixpanel property key for how many contacts the Action Center lookup matched. */
export const MIXPANEL_CANDIDATE_COUNT_PROP = 'Candidate Count';

/**
 * Mixpanel's own auto-captured page URL property.
 *
 * mixpanel-browser puts window.location.href on every event, and the Action Center URL
 * carries ?phone_number= and &name=. Caller-supplied properties are extended over the
 * automatic ones, so setting this explicitly replaces the full URL before it is sent.
 */
export const MIXPANEL_CURRENT_URL_PROP = '$current_url';

/** Mirrors the server ActionCenterLookupOutcome enum returned by the lookup API. */
export type ActionCenterMatchOutcome = 'NONE' | 'ONE' | 'MULTIPLE';

export type ActionCenterLookupAnalytics = {
  matchOutcome: ActionCenterMatchOutcome;
  candidateCount: number;
};

export type CrmEmailSentFrom = 'template' | 'manual';

export type CrmEmailSendAnalytics = {
  sent_from: CrmEmailSentFrom;
  template_name: string | null;
  /** Null on an English send. A language code means the reviewed translation was sent. */
  translation_language?: string | null;
  /** True only when this send follows a failed translation preview of the same draft. */
  sent_after_failed_preview?: boolean;
};

export type TranslationPreviewOutcome = 'success' | 'failed';

export type TranslationPreviewAnalytics = {
  target_language: string | null;
  outcome: TranslationPreviewOutcome;
  model_version: string | null;
  prompt_version: string | null;
};

/** Which staff surface performed the close. */
export type SubmissionCloseMethod = 'detail_page' | 'bulk_action';

export type SubmissionClosedAnalytics = {
  closure_reason: string | null;
  close_method: SubmissionCloseMethod;
  notified: boolean;
};

export type StaffMixpanelSubmissionProps = Record<string, string | number | boolean | null>;

/** Staff Mixpanel Agency Name: JWT profile.agencyName (Agency.name) */
export const getStaffMixpanelAgencyName = (): string | null => {
  try {
    return getUserProfile()?.agencyName ?? null;
  } catch {
    return null;
  }
};

/** Agency Name props for staff Mixpanel events (including session start/end). */
export const getStaffMixpanelAgencyProps = (): Record<string, string | null> => ({
  [MIXPANEL_AGENCY_NAME_PROP]: getStaffMixpanelAgencyName(),
});

export const getStaffTextMixpanelRole = (profile: IUserProfile | null): StaffTextMixpanelRole => {
  const scopes = profile?.scopes;
  if (scopes?.superAdmin) {
    return 'super_admin';
  }
  if (scopes?.comcateAdmin) {
    return 'comcate_admin';
  }
  if (scopes?.siteAdmin) {
    return 'site_admin';
  }
  if (scopes?.crm?.productAdmin) {
    return 'crm_product_admin';
  }

  return 'crm_user';
};

const getStaffProfileOrNull = (): IUserProfile | null => {
  try {
    return getUserProfile();
  } catch {
    return null;
  }
};

/** Agency ID and User ID from the staff JWT profile, for staff events that identify the actor. */
export const getStaffMixpanelIdentityProps = (
  profile: IUserProfile | null = getStaffProfileOrNull()
): Record<string, number | null> => ({
  [MIXPANEL_AGENCY_ID_PROP]: profile?.agencyId ?? null,
  [MIXPANEL_USER_ID_PROP]: profile?.id ?? null,
});

export const getStaffTextMixpanelContext = (): Record<string, string | number | null> => {
  const profile = getStaffProfileOrNull();

  return {
    ...getStaffMixpanelIdentityProps(profile),
    [MIXPANEL_USER_ROLE_PROP]: getStaffTextMixpanelRole(profile),
    ...getStaffMixpanelAgencyProps(),
  };
};

export const getStaffTextPromoProps = (): Record<string, string | number | null> => ({
  'Device Details': getFormattedDeviceDetails(),
  ...getStaffTextMixpanelContext(),
});

export const getStaffTextSectionOpenedProps = (
  signedUpState: StaffTextEnrollmentState
): Record<string, string | number | null> => ({
  ...getStaffTextMixpanelContext(),
  [MIXPANEL_SIGNED_UP_STATE_PROP]: signedUpState,
});

export const trackTextAPhotoSectionOpened = (signedUpState: StaffTextEnrollmentState): void => {
  try {
    const { TEXT_A_PHOTO_SECTION_OPENED } = getLabels();
    track(TEXT_A_PHOTO_SECTION_OPENED, getStaffTextSectionOpenedProps(signedUpState));
  } catch {
    // The profile section already rendered. Analytics delivery is not recoverable here.
  }
};

/** Device Details + Agency Name + submission_id for submission-scoped staff CRM events. */
export const getStaffMixpanelSubmissionProps = (submissionId: number): StaffMixpanelSubmissionProps => ({
  'Device Details': getFormattedDeviceDetails(),
  ...getStaffMixpanelAgencyProps(),
  [MIXPANEL_SUBMISSION_ID_PROP]: submissionId,
});

/** Props for MANUAL_EMAIL_SENT_FROM_CRM (shared by all CRM email send surfaces). */
export const getManualEmailSentFromCrmProps = (
  submissionId: number,
  analytics: CrmEmailSendAnalytics = { sent_from: 'manual', template_name: null }
): StaffMixpanelSubmissionProps => ({
  ...getStaffMixpanelSubmissionProps(submissionId),
  sent_from: analytics.sent_from,
  template_name: analytics.template_name,
  translation_language: analytics.translation_language ?? null,
  sent_after_failed_preview: analytics.sent_after_failed_preview ?? false,
});

/**
 * Track MANUAL_EMAIL_SENT_FROM_CRM with canonical agency + submission/template context.
 * Used by EmailTile, EmailExpandedView, and ComposeEmailModal.
 */
export const trackManualEmailSentFromCrm = (submissionId: number, analytics?: CrmEmailSendAnalytics): void => {
  const { MANUAL_EMAIL_SENT_FROM_CRM } = getLabels();
  track(MANUAL_EMAIL_SENT_FROM_CRM, getManualEmailSentFromCrmProps(submissionId, analytics));
};

/**
 * Props for TRANSLATION_PREVIEW_COMPLETED. Metadata only: language, outcome, and the
 * model and prompt version. Message text is not a property and must not be added here.
 */
export const getTranslationPreviewCompletedProps = (
  submissionId: number,
  analytics: TranslationPreviewAnalytics
): StaffMixpanelSubmissionProps => ({
  ...getStaffMixpanelSubmissionProps(submissionId),
  target_language: analytics.target_language,
  outcome: analytics.outcome,
  model_version: analytics.model_version,
  prompt_version: analytics.prompt_version,
});

/**
 * Track one TRANSLATION_PREVIEW_COMPLETED per preview attempt. A Mixpanel failure must not
 * leave the composer stuck on the loading preview.
 */
export const trackTranslationPreviewCompleted = (
  submissionId: number,
  analytics: TranslationPreviewAnalytics
): void => {
  try {
    const { TRANSLATION_PREVIEW_COMPLETED } = getLabels();
    track(TRANSLATION_PREVIEW_COMPLETED, getTranslationPreviewCompletedProps(submissionId, analytics));
  } catch {
    // The preview already finished. Analytics delivery is not recoverable here.
  }
};

/** Props for SUBMISSION_CLOSED. `closure_reason` is the canonical reason code the server resolved. */
export const getSubmissionClosedProps = (
  submissionId: number,
  analytics: SubmissionClosedAnalytics
): StaffMixpanelSubmissionProps => ({
  ...getStaffMixpanelSubmissionProps(submissionId),
  [MIXPANEL_CLOSURE_REASON_PROP]: analytics.closure_reason,
  [MIXPANEL_CLOSE_METHOD_PROP]: analytics.close_method,
  [MIXPANEL_NOTIFIED_PROP]: analytics.notified,
});

/**
 * Single emit point for SUBMISSION_CLOSED. Swallows any Mixpanel failure so a close never
 * depends on analytics delivery: on the detail page a throw here would leave the close
 * promise unresolved, and on the listing it would skip the toast and list refresh.
 */
const emitSubmissionClosed = (submissionId: number, analytics: SubmissionClosedAnalytics): void => {
  try {
    const { SUBMISSION_CLOSED } = getLabels();
    track(SUBMISSION_CLOSED, getSubmissionClosedProps(submissionId, analytics));
  } catch {
    // Deliberately swallowed: the close already succeeded and analytics is not recoverable here.
  }
};

/** Track ASSIGNEE_CHANGED after a successful assignee update on a submission. */
export const trackAssigneeChanged = (submissionId: number): void => {
  const { ASSIGNEE_CHANGED } = getLabels();
  track(ASSIGNEE_CHANGED, getStaffMixpanelSubmissionProps(submissionId));
};

/**
 * Track SUBMISSION_CLOSED after a successful close from the submission detail page.
 * notified stays true here; CDS-7007 "close without notifying" owns the false case on this
 * surface and keeps the same event name.
 */
export const trackSubmissionClosed = (submissionId: number, closureReason: string | null): void => {
  emitSubmissionClosed(submissionId, {
    closure_reason: closureReason,
    close_method: 'detail_page',
    notified: true,
  });
};

/**
 * Track one SUBMISSION_CLOSED per submission the server actually transitioned, so already-closed
 * rows in a bulk selection emit nothing. Bulk close is non-notifying, hence notified: false.
 */
export const trackBulkSubmissionsClosed = (
  transitionedIssueIds: number[] | undefined,
  closureReason: string | null
): void => {
  (transitionedIssueIds || []).forEach((submissionId) =>
    emitSubmissionClosed(submissionId, {
      closure_reason: closureReason,
      close_method: 'bulk_action',
      notified: false,
    })
  );
};

/** Track INTERNAL_COMMENT_ADDED after a successful note create (not edit). */
export const trackInternalCommentAdded = (submissionId: number): void => {
  const { INTERNAL_COMMENT_ADDED } = getLabels();
  track(INTERNAL_COMMENT_ADDED, getStaffMixpanelSubmissionProps(submissionId));
};

/**
 * The complete property allow-list for every Action Center event: agency, match
 * outcome, candidate count, and device context, plus the $current_url override
 * that strips the query string Mixpanel would otherwise capture on its own.
 *
 * These events measure lookup-to-submission conversion, so they must never carry
 * resident PII or anything that could re-identify a caller. Phone number, contact
 * name, contact ID, the full page URL, the raw lookup payload, and any carrier or
 * provider inference are all excluded. submission_id and lookup_id are excluded
 * too, even though other staff CRM events carry submission_id -- a submission ID
 * joins back to the resident record, and lookup ID stays in server logs only.
 *
 * Build these props only through this function. Callers must not spread extra
 * keys onto the result.
 */
export const getActionCenterLookupProps = (analytics: ActionCenterLookupAnalytics): StaffMixpanelSubmissionProps => ({
  'Device Details': getFormattedDeviceDetails(),
  ...getStaffMixpanelAgencyProps(),
  [MIXPANEL_MATCH_OUTCOME_PROP]: analytics.matchOutcome,
  [MIXPANEL_CANDIDATE_COUNT_PROP]: analytics.candidateCount,
  // Path only: keeps which page fired the event, drops phone_number and name.
  [MIXPANEL_CURRENT_URL_PROP]: window.location.pathname,
});

/** Track ACTION_CENTER_LOOKUP_COMPLETED once per resolved Action Center phone lookup. */
export const trackActionCenterLookupCompleted = (analytics: ActionCenterLookupAnalytics): void => {
  const { ACTION_CENTER_LOOKUP_COMPLETED } = getLabels();
  track(ACTION_CENTER_LOOKUP_COMPLETED, getActionCenterLookupProps(analytics));
};

/** Track ACTION_CENTER_SUBMISSION_STARTED when the submission form opens after a lookup. */
export const trackActionCenterSubmissionStarted = (analytics: ActionCenterLookupAnalytics): void => {
  const { ACTION_CENTER_SUBMISSION_STARTED } = getLabels();
  track(ACTION_CENTER_SUBMISSION_STARTED, getActionCenterLookupProps(analytics));
};

/** Track ACTION_CENTER_SUBMISSION_CREATED after a submission is successfully created. */
export const trackActionCenterSubmissionCreated = (analytics: ActionCenterLookupAnalytics): void => {
  const { ACTION_CENTER_SUBMISSION_CREATED } = getLabels();
  track(ACTION_CENTER_SUBMISSION_CREATED, getActionCenterLookupProps(analytics));
};

export type SubmissionListingDownloadProps = Record<string, unknown>;

/**
 * The complete property allow-list for SUBMISSION_LISTING_DOWNLOAD_CLICKED. Fields are picked one
 * by one so nothing else on the analytics object reaches Mixpanel. $current_url is the path only,
 * because the listing URL carries searchQuery and customerIds.
 */
export const getSubmissionListingDownloadProps = (
  analytics: ISubmissionListingDownloadAnalytics
): SubmissionListingDownloadProps => ({
  'Device Details': getFormattedDeviceDetails(),
  ...getStaffMixpanelIdentityProps(),
  ...getStaffMixpanelAgencyProps(),
  format: analytics.format,
  saved_view_name: analytics.saved_view_name,
  applied_filters: analytics.applied_filters,
  non_default_filters: analytics.non_default_filters,
  has_keyword_search: analytics.has_keyword_search,
  selected_columns: analytics.selected_columns,
  result_count: analytics.result_count,
  exported_row_count: analytics.exported_row_count,
  [MIXPANEL_CURRENT_URL_PROP]: window.location.pathname,
});

/** Track one SUBMISSION_LISTING_DOWNLOAD_CLICKED per download. A download never depends on analytics. */
export const trackSubmissionListingDownloadClicked = (analytics: ISubmissionListingDownloadAnalytics): void => {
  try {
    const { SUBMISSION_LISTING_DOWNLOAD_CLICKED } = getLabels();
    track(SUBMISSION_LISTING_DOWNLOAD_CLICKED, getSubmissionListingDownloadProps(analytics));
  } catch {
    // The download already started. Analytics delivery is not recoverable here.
  }
};

const getScreenDPI = () => {
  const dpi = window.devicePixelRatio * 96; // 96 is the standard DPI for most screens

  return dpi;
};

export const track = (name: string, props?: any) => {
  if (process.env.ENVIRONMENT && process.env.ENVIRONMENT !== 'test') {
    mixpanel.track(name, props);
  }
};

export const trackTime = (name: string, props?: any) => {
  if (process.env.ENVIRONMENT && process.env.ENVIRONMENT !== 'test') {
    mixpanel.time_event(getLabels()[name], props);
    mixpanel.track(getLabels()[name], props);
  }
};

export const trackOnElevioPortal = (name: string, props?: object) => {
  if (process.env.ENVIRONMENT && process.env.ENVIRONMENT !== 'test') {
    const properties = {
      ...props,
      screen_dpi: getScreenDPI(),
    };
    mixpanel.elevioMixpanel.track(name, properties);
  }
};
